import { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, X, Upload, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminAxios } from './AdminLayout';

function ClientModal({ client, onClose, onSaved }) {
  const isEdit = !!client?.id;
  const [form, setForm] = useState({
    full_name: '', company_name: '', email: '', phone: '', address: '',
    country: '', vat_number: '', business_type: '', product_interests: '',
    notes: '', subscribed: true, source: 'manual',
    ...(client || {}),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await adminAxios().put(`/api/admin/clients/${client.id}`, form);
      } else {
        await adminAxios().post('/api/admin/clients', form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'full_name', label: 'Full Name', required: true },
    { key: 'company_name', label: 'Company Name', required: true },
    { key: 'email', label: 'Email', type: 'email', required: true },
    { key: 'phone', label: 'Phone' },
    { key: 'address', label: 'Address' },
    { key: 'country', label: 'Country' },
    { key: 'vat_number', label: 'VAT Number' },
    { key: 'business_type', label: 'Business Type' },
    { key: 'product_interests', label: 'Product Interests' },
    { key: 'notes', label: 'Notes', multiline: true },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 m-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="client-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-outfit text-xl font-bold text-slate-900">{isEdit ? 'Edit Client' : 'Add New Client'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map(f => (
              <div key={f.key} className={f.multiline ? 'sm:col-span-2' : ''}>
                <label className="font-manrope text-xs font-medium text-slate-600 block mb-1">{f.label}{f.required && ' *'}</label>
                {f.multiline ? (
                  <textarea
                    value={form[f.key] || ''}
                    onChange={e => setForm(p => ({...p, [f.key]: e.target.value}))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 font-manrope text-sm resize-none h-20"
                  />
                ) : (
                  <input
                    type={f.type || 'text'}
                    value={form[f.key] || ''}
                    onChange={e => setForm(p => ({...p, [f.key]: e.target.value}))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 font-manrope text-sm"
                    required={f.required}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              checked={form.subscribed}
              onChange={e => setForm(p => ({...p, subscribed: e.target.checked}))}
              className="w-4 h-4 rounded border-slate-300 text-[#0096D6]"
              id="subscribed"
            />
            <label htmlFor="subscribed" className="font-manrope text-sm text-slate-700">Subscribed to product offers</label>
          </div>

          {error && <p className="text-red-500 text-sm font-manrope">{error}</p>}

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl border border-slate-200 font-manrope text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold disabled:opacity-50">
              {saving ? 'Saving...' : isEdit ? 'Update Client' : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [modalClient, setModalClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState('all');

  const fetchClients = async (group) => {
    try {
      const g = group || activeGroup;
      var url = '/api/admin/clients';
      if (g === 'UK' || g === 'INTERNATIONAL' || g === 'GB_MAINLAND' || g === 'NORTHERN_IRELAND') {
        url = '/api/admin/clients?market=' + g;
      } else if (g !== 'all') {
        url = '/api/admin/clients/by-group/' + g;
      }
      const res = await adminAxios().get(url);
      setClients(res.data);
    } catch (e) {}
    setLoading(false);
  };

  const fetchGroups = async () => {
    try {
      const res = await adminAxios().get('/api/admin/clients/groups');
      setGroups(res.data.groups || []);
    } catch (e) {}
  };

  useEffect(() => { fetchClients(); fetchGroups(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    await adminAxios().delete(`/api/admin/clients/${id}`);
    fetchClients();
  };

  const filtered = clients.filter(c =>
    (c.full_name + c.company_name + c.email + c.country).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="admin-clients-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h2 className="font-outfit text-2xl font-bold text-slate-900">Client Distribution List</h2>
        <div className="flex items-center gap-2">
          <Link to="/admin/clients/import" className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#0096D6] text-[#0096D6] font-manrope text-sm font-semibold hover:bg-[#0096D6]/5" data-testid="bulk-import-button">
            <Sparkles className="w-4 h-4" /> AI Bulk Import
          </Link>
          <button
            onClick={() => { setModalClient(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold"
            data-testid="add-client-button"
          >
            <Plus className="w-4 h-4" /> Add Client
          </button>
        </div>
      </div>

      {/* Group Filter */}
      {groups.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {groups.map(g => (
            <button
              key={g.id}
              onClick={() => { setActiveGroup(g.id); setLoading(true); fetchClients(g.id); }}
              className={`px-3 py-1.5 rounded-full font-manrope text-xs font-medium transition-colors ${activeGroup === g.id ? 'bg-[#0096D6] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {g.name} ({g.count})
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search clients..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 font-manrope text-sm"
          data-testid="client-search-input"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-slate-500 font-manrope">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400 font-manrope">No clients found</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 font-manrope font-semibold text-slate-600">Name</th>
                  <th className="text-left px-4 py-3 font-manrope font-semibold text-slate-600">Company</th>
                  <th className="text-left px-4 py-3 font-manrope font-semibold text-slate-600">Email</th>
                  <th className="text-left px-4 py-3 font-manrope font-semibold text-slate-600">Country</th>
                  <th className="text-left px-4 py-3 font-manrope font-semibold text-slate-600">Market</th>
                  <th className="text-left px-4 py-3 font-manrope font-semibold text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 font-manrope font-semibold text-slate-600">Source</th>
                  <th className="text-right px-4 py-3 font-manrope font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(client => (
                  <tr key={client.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-manrope font-medium text-slate-900">{client.full_name}</td>
                    <td className="px-4 py-3 font-manrope text-slate-600">{client.company_name}</td>
                    <td className="px-4 py-3 font-manrope text-slate-600">{client.email}</td>
                    <td className="px-4 py-3 font-manrope text-slate-600">{client.country || '-'}</td>
                    <td className="px-4 py-3">
                      {client.market_region === 'UK' ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">{client.market_subregion === 'NORTHERN_IRELAND' ? 'UK – NI' : 'UK'}</span>
                      ) : client.market_region === 'INTERNATIONAL' ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">International</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">Unclassified</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${client.subscribed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {client.subscribed ? 'Subscribed' : 'Unsubscribed'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-manrope text-slate-500 text-xs">{client.source}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => { setModalClient(client); setShowModal(true); }} className="p-2 hover:bg-slate-100 rounded-lg" title="Edit">
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </button>
                        <button onClick={() => handleDelete(client.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Delete">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <ClientModal
          client={modalClient}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchClients(); }}
        />
      )}
    </div>
  );
}
